import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { BranchIcon, LoadingIcon } from '@/components/icons';
import { PromptInstructionHistory } from '@/models/PromptInstruction';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { XMarkIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { PromptInstructionHistoryWithVersion } from '../workspace/PromptsWorkspace';
import { useLayoutEffect, useRef, useState } from 'react';

type PromptsHistorySidebarProps = {
  history: PromptInstructionHistory[];
  isLoading: boolean;
  onClose: () => void;
  onSelectVersion: (version: PromptInstructionHistoryWithVersion) => void;
  selectedVersion?: PromptInstructionHistoryWithVersion;
};

export default function PromptsHistorySidebar({
  history,
  isLoading,
  onClose,
  onSelectVersion,
  selectedVersion,
}: PromptsHistorySidebarProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts.history',
  });

  const [historyListHeight, setHistoryListHeight] = useState(0);

  const historyListRef = useRef<HTMLUListElement>(null);

  useLayoutEffect(() => {
    if (historyListRef.current) {
      setHistoryListHeight(historyListRef.current.scrollHeight);
    }
  }, [history]);

  return (
    <div className="flex flex-col gap-4 p-3 border border-primary border-l-0 rounded-xl rounded-tl-none rounded-bl-none bg-base-100 min-w-[150px] w-1/3 max-w-[300px]">
      <header className="flex items-center justify-between">
        <Text text={t('title')} format="poppins-600" className="text-primary" />
        <IconButton
          icon={<XMarkIcon className="w-5 h-5" />}
          onClick={onClose}
          className="text-base-content hover:text-primary"
        />
      </header>

      <ConditionalRenderer condition={!history.length && !isLoading}>
        <Text text={t('noHistory')} className="text-sm" />
      </ConditionalRenderer>

      <ConditionalRenderer
        condition={!isLoading && history.length > 0}
        fallback={
          <div className="flex justify-center items-center py-8">
            <ConditionalRenderer
              condition={isLoading}
              fallback={<Text text={t('noHistory')} className="text-sm" />}
            >
              <LoadingIcon className="w-8 h-8 text-primary" />
            </ConditionalRenderer>
          </div>
        }
      >
        <ul
          className={twJoin(
            'relative flex flex-col gap-2.5 overflow-y-auto max-h-full',
            scrollBarYClassName,
          )}
          ref={historyListRef}
        >
          {history.map((item, index, arr) => (
            <HistoryItem
              key={item.historyId}
              history={item}
              isSelected={selectedVersion?.historyId === item.historyId}
              version={arr.length - index}
              isLastVersion={index === 0}
              onSelectVersion={onSelectVersion}
            />
          ))}
          <div
            className="absolute top-0 z-[0] left-[18px] w-0.5 bg-secondary"
            style={{ height: `${historyListHeight}px` }}
          />
        </ul>
      </ConditionalRenderer>
    </div>
  );
}

type HistoryItemProps = {
  history: PromptInstructionHistory;
  isSelected: boolean;
  version: number;
  isLastVersion: boolean;
  onSelectVersion: (version: PromptInstructionHistoryWithVersion) => void;
};

const HistoryItem = ({
  history,
  onSelectVersion,
  version,
  isSelected,
  isLastVersion,
}: HistoryItemProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'prompts.history',
  });

  const getVersionLabel = (version: number) => {
    if (isLastVersion)
      return `${t('version', { version: `${version}.0` })} (${t('latest')})`;
    return t('version', { version: `${version}.0` });
  };
  return (
    <button
      key={history.historyId}
      onClick={() => onSelectVersion({ ...history, version })}
      className={twJoin(
        'flex items-center z-[1] gap-3 p-1 pr-1.5 rounded-xl border border-neutral-content hover:border-secondary transition-all cursor-pointer bg-base-100',
        isSelected && 'border-secondary',
        isLastVersion && 'filter grayscale pointer-events-none',
      )}
    >
      <div className="flex items-center justify-center bg-secondary-content rounded-full w-8 h-8 shrink-0">
        <BranchIcon
          className="w-4 h-4 text-secondary shrink-0"
          strokeWidth={2}
        />
      </div>
      <div className="flex grow items-start justify-between flex-wrap gap-1">
        <Text
          text={getVersionLabel(version)}
          className="text-primary lg:text-sm text-xs text-left"
          format="poppins-600"
        />
        <Text
          text={moment(history.historyDate).format('DD/MM/YYYY HH:mm')}
          className="lg:text-sm text-xs text-base-content/70 text-left"
        />
      </div>
    </button>
  );
};
